import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Badge, Modal } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import SearchSelect from './SearchSelect';
import { refreshWhatsAppBadge } from './WhatsAppBadge';
import './WhatsAppInbox.css';
import { formatDateTime } from '../utils/dates';

const POLL_MS = 10 * 1000;
const time = (d) => formatDateTime(d);
const phoneText = (p) => `+${p}`;
const VIEWS = [['waiting', 'Esperan a una persona'], ['human', 'Atendidas por el equipo'], ['all', 'Todas']];
const TICKS = { sent: '✓', delivered: '✓✓', read: '✓✓' };

// An admin links a WhatsApp number to a client by hand (the customer can also do it themselves with a code by email).
const LinkClientModal = ({ conversation, onClose, onDone }) => {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(conversation.client?.id || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { API.get('/clients').then(r => setClients(r.data)).catch(() => setError('No se pudo cargar la lista de clientes.')); }, []);
  const save = async (value) => {
    setBusy(true); setError('');
    try { await API.put(`/whatsapp-inbox/conversations/${conversation.id}/client`, { clientId: value }); onDone(); } catch (err) {
      setError(err.response?.data?.message || 'No se pudo guardar.'); setBusy(false);
    }
  };
  return (
    <Modal show onHide={busy ? undefined : onClose} centered>
      <Modal.Header closeButton={!busy}><Modal.Title>Vincular WhatsApp a un cliente</Modal.Title></Modal.Header>
      <Modal.Body>
        <p className="small text-muted">
          Quien use el WhatsApp {phoneText(conversation.phone)} podrá consultar el balance, las facturas y los pedidos de ese cliente, y hacer pedidos a su nombre.
          Vincúlalo solo si estás seguro de que es de ese cliente.
        </p>
        {error && <div className="alert alert-danger py-2">{error}</div>}
        <SearchSelect options={clients} value={clientId} getKey={c => c._id} getLabel={c => `${c.name}${c.rnc ? ` — RNC ${c.rnc}` : ''}`}
          onSelect={c => setClientId(c._id)} placeholder="Buscar cliente..." />
      </Modal.Body>
      <Modal.Footer>
        {conversation.client && <Button variant="outline-danger" className="me-auto" disabled={busy} onClick={() => save(null)}>Desvincular</Button>}
        <Button variant="outline-secondary" disabled={busy} onClick={onClose}>Cancelar</Button>
        <Button disabled={busy || !clientId || clientId === conversation.client?.id} onClick={() => save(clientId)}>{busy ? 'Guardando...' : 'Vincular'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

// WhatsApp: every conversation on the company's WhatsApp line. The assistant answers on its own; when a customer asks
// for a person (or the assistant can't help) the conversation waits here — someone takes it, replies, and hands it back.
const WhatsAppInboxPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState(params.get('view') || 'waiting');
  const [query, setQuery] = useState('');
  const [list, setList] = useState(null);
  const [status, setStatus] = useState(null);
  const [selectedId, setSelectedId] = useState(params.get('id') || '');
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [linking, setLinking] = useState(false);
  const bottomRef = useRef(null);

  const loadList = useCallback(() => API.get('/whatsapp-inbox/conversations', { params: { view, q: query || undefined } })
    .then(r => setList(r.data)).catch(() => setError('No se pudo cargar la bandeja de WhatsApp.')), [view, query]);
  const loadThread = useCallback(() => (selectedId
    ? API.get(`/whatsapp-inbox/conversations/${selectedId}`).then(r => setThread(r.data)).catch(() => setThread(null))
    : Promise.resolve()), [selectedId]);

  useEffect(() => { API.get('/whatsapp-inbox/summary').then(r => setStatus(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { setThread(null); loadThread().then(refreshWhatsAppBadge); }, [loadThread]);
  useEffect(() => {
    const timer = setInterval(() => { loadList(); loadThread(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [loadList, loadThread]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'end' }); }, [thread?.messages?.length]);

  const choose = (id) => {
    setSelectedId(id); setDraft(''); setError('');
    const next = new URLSearchParams(params); next.set('id', id); setParams(next, { replace: true });
  };
  const changeView = (v) => {
    setView(v);
    const next = new URLSearchParams(params); next.set('view', v); setParams(next, { replace: true });
  };

  const act = async (label, fn) => {
    setBusy(label); setError('');
    try { await fn(); await Promise.all([loadThread(), loadList()]); refreshWhatsAppBadge(); } catch (err) {
      setError(err.response?.data?.message || 'No se pudo completar la acción.');
    } finally { setBusy(''); }
  };
  const send = () => act('reply', async () => {
    await API.post(`/whatsapp-inbox/conversations/${selectedId}/reply`, { text: draft });
    setDraft('');
  });

  const c = thread?.conversation;

  return (
    <div className="container-fluid p-4 wa-page">
      <h2 className="app-title mb-2" style={{ textAlign: 'left' }}>WhatsApp</h2>
      <div className="small text-muted mb-3">
        Cilindrito, el asistente virtual, responde solo a los clientes por WhatsApp. Cuando alguien pide hablar con una persona, o el asistente no puede ayudarle, la conversación
        espera aquí: atiéndela, respóndele y devuélvela a Cilindrito cuando termines. WhatsApp permite responder hasta 24 horas después del último mensaje del cliente.
      </div>
      {status && !status.configured && <div className="alert alert-warn">La bandeja de WhatsApp no está configurada en este servidor (WHATSAPP_SERVICE_URL y WHATSAPP_INTEGRATION_KEY en el archivo .env).</div>}
      {status && status.configured && status.reachable === false && <div className="alert alert-danger">El servicio de WhatsApp no responde en este momento.</div>}
      {status && status.configured && status.botEnabled === false && <div className="alert alert-note">El asistente está apagado: todas las conversaciones esperan a una persona.</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="wa-layout">
        <aside className="wa-list dashboard-card">
          <div className="wa-views">
            {VIEWS.map(([key, label]) => (
              <button key={key} type="button" className={`btn btn-sm ${view === key ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => changeView(key)}>{label}</button>
            ))}
          </div>
          <Form.Control size="sm" className="mb-2" placeholder="Buscar por cliente, nombre o número" value={query} onChange={e => setQuery(e.target.value)} />
          {list && list.length === 0 && <div className="small text-muted p-2">{view === 'waiting' ? 'Nadie está esperando a una persona.' : 'No hay conversaciones.'}</div>}
          {list?.map(item => (
            <button key={item.id} type="button" className={`wa-item${item.id === selectedId ? ' active' : ''}`} onClick={() => choose(item.id)}>
              <div className="d-flex justify-content-between gap-2">
                <strong className="wa-name">{item.client?.name || (item.staff && `${item.staff.username} (equipo)`) || item.profileName || phoneText(item.phone)}</strong>
                <span className="small text-muted text-nowrap">{item.lastMessageAt ? time(item.lastMessageAt) : ''}</span>
              </div>
              <div className="small text-muted wa-sub">{item.client || item.staff ? `${item.profileName ? `${item.profileName} · ` : ''}${phoneText(item.phone)}` : `${phoneText(item.phone)} · sin vincular`}</div>
              <div className="d-flex justify-content-between gap-2 align-items-center">
                <span className="small wa-preview">{item.lastPreview}</span>
                <span className="d-flex gap-1">
                  {item.waiting && <Badge bg="warning" text="dark">Espera</Badge>}
                  {item.takenBy && <Badge bg="secondary">{item.takenBy}</Badge>}
                  {item.unread > 0 && <Badge bg="info">{item.unread}</Badge>}
                </span>
              </div>
            </button>
          ))}
        </aside>

        <section className="wa-thread dashboard-card">
          {!selectedId && <div className="text-muted p-4">Elige una conversación.</div>}
          {selectedId && !thread && <div className="text-muted p-4">Cargando...</div>}
          {c && (
            <>
              <header className="wa-thread-head">
                <div style={{ minWidth: 0 }}>
                  <div className="wa-name"><strong>{c.client?.name || (c.staff && `${c.staff.username} (equipo)`) || c.profileName || phoneText(c.phone)}</strong></div>
                  <div className="small text-muted">
                    {phoneText(c.phone)}{c.profileName ? ` · perfil: ${c.profileName}` : ''} · {c.staff ? `número de ${c.staff.username} (${c.staff.role === 'admin' ? 'administrador: recibe los números de la empresa' : 'ventas'})` : c.client ? `vinculado${c.linkedBy === 'codigo' ? ' por el cliente (código por correo)' : c.linkedBy ? ` por ${c.linkedBy}` : ''}` : 'sin vincular a un cliente'}
                  </div>
                  <div className="small mt-1">
                    {c.mode === 'bot'
                      ? <span className="text-muted">Lo atiende Cilindrito, el asistente virtual.</span>
                      : c.takenBy ? <span>Lo atiende <strong>{c.takenBy}</strong>; Cilindrito no responde.</span>
                        : <span className="text-warning">Espera a una persona{c.handoffReason ? `: ${c.handoffReason}` : ''}.</span>}
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-start">
                  {isAdmin && <Button size="sm" variant="outline-secondary" onClick={() => setLinking(true)}>{c.client ? 'Cambiar cliente' : 'Vincular cliente'}</Button>}
                  {(c.mode === 'bot' || !c.takenBy) && <Button size="sm" variant="outline-primary" disabled={!!busy} onClick={() => act('take', () => API.post(`/whatsapp-inbox/conversations/${c.id}/take`))}>Atender</Button>}
                  {c.mode === 'human' && <Button size="sm" variant="outline-secondary" disabled={!!busy} onClick={() => act('release', () => API.post(`/whatsapp-inbox/conversations/${c.id}/release`))}>Devolver a Cilindrito</Button>}
                </div>
              </header>

              <div className="wa-messages">
                {thread.messages.map(m => (
                  <div key={m.id} className={`wa-msg ${m.direction === 'in' ? 'in' : m.author === 'bot' ? 'bot' : 'staff'}`}>
                    {m.direction === 'out' && <div className="wa-author">{m.author === 'bot' ? 'Cilindrito' : m.author}</div>}
                    {m.kind === 'document' ? <div>📄 {m.document?.filename}{m.document?.caption && m.document.caption !== m.document.filename ? ` — ${m.document.caption}` : ''}</div>
                      : m.kind !== 'text' ? <div className="text-muted fst-italic">[{m.kind}: el asistente solo lee texto]</div>
                        : <div className="wa-text">{m.text}</div>}
                    <div className="wa-meta">
                      {time(m.at)}
                      {m.direction === 'out' && m.status && m.status !== 'failed' && <span className={m.status === 'read' ? 'wa-read' : ''}> {TICKS[m.status]}</span>}
                      {m.status === 'failed' && <span className="text-danger"> · no se envió{m.error ? `: ${m.error}` : ''}</span>}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              <footer className="wa-compose">
                {c.canReply ? (
                  <>
                    <Form.Control as="textarea" rows={2} maxLength={4000} value={draft} placeholder="Escribe una respuesta (al enviar, atiendes la conversación y el asistente deja de responder)"
                      onChange={e => setDraft(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && draft.trim()) { e.preventDefault(); send(); } }} />
                    <Button disabled={!draft.trim() || !!busy} onClick={send}>{busy === 'reply' ? 'Enviando...' : 'Enviar'}</Button>
                  </>
                ) : (
                  <div className="small text-muted">Pasaron más de 24 horas desde el último mensaje del cliente: WhatsApp no permite escribirle hasta que vuelva a escribir.</div>
                )}
              </footer>
            </>
          )}
        </section>
      </div>

      {linking && c && <LinkClientModal conversation={c} onClose={() => setLinking(false)} onDone={() => { setLinking(false); loadThread(); loadList(); }} />}
    </div>
  );
};

export default WhatsAppInboxPage;
